import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, makeStyles, TableHead, TableRow, TableCell, Table, TableBody } from '@material-ui/core';
import _ from 'lodash';
import Text from '../../../../../components/Text';
import StyleButton from '../../../../../components/StyleButton';
import {  EditIcon, EyeHideIcon2, EyeIcon } from '../../../../../components/Icon';
import { eadmissionAPI } from '../../../../../apis';
import Logo from '../../../../../static/images/logo.png';
import TitleBox from '../../../../Admission/components/TitleBox.js';
import { wardAssignByMapping } from '../constant';
import StopStation from './StopStation';
import EditWorkflow from './popups/EditWorkflow.js';

const useStyles = makeStyles((theme) => ({
  table: {
    borderSpacing: '0',
    borderCollapse: 'unset',
    '& .MuiTableCell-head': {
      color: '#538FFF',
      fontFamily: 'NotoSans-Bold',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      borderBottom: 'none !important',
      padding: '0 1rem 1rem 0',
    },
    '& .MuiTableCell-body': {
      borderBottom: 'solid 1px #E4E7EB',
      verticalAlign: 'middle',
      paddingRight: '1rem',
      paddingLeft: '0',
      color: '#2E4469',
      fontSize: '1rem',
      fontFamily: 'NotoSans-Medium',
      height: '5.69rem',
      position: 'relative',
    },
    '& .MuiTableBody-root': {
      '&>tr:last-child': {
        '& .MuiTableCell-body': {
          borderBottom: 'unset',
        },
      },
    },
  },
  busStopStation: {
    borderBottom: 'none !important',
    padding: '0 !important',
  },
  eyeIcon: {
    fontSize: '1.25rem',
    color: '#4C93FF',
  },
  eyeHideIcon: {
    fontSize: '1.125rem',
    color: '#C0C6CB',
  },
  logo: {
    width: '2.5625rem',
    height: '2.5625rem',
  },
}));

const Workflow = () => {
  const classes = useStyles();
  const [data, setData] = useState();
  const [edit, setEdit] = useState(false);

  const getWorkflowList = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getWorkflowList();
      setData(result);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    getWorkflowList();
  }, [getWorkflowList]);

  const workflow = useMemo(() => {
    if (!data || JSON.stringify(data) === '{}') {
      return [];
    }

    const wardAssignBy = _.map(data.specialty, (item) => ({
      title: item.specialtyCode,
      subTitle: wardAssignByMapping[item.wardAssignBy],
      key: item.specialtyId,
    }));

    return [
      {
        stopStation: 'Admission Decision',
        display: 'Y',
        system: 'eAED',
        by: [
          {
            title: data.workflow.aedWardName,
          },
        ],
        color: 'brown',
      },
      {
        stopStation: 'Admission Ready',
        display: data.workflow.isAdmReadyReq,
        system: 'logo',
        by: [
          {
            title: data.workflow.aedWardName,
          },
        ],
        color: 'yellowSolid',
      },
      {
        stopStation: data.workflow.wardAssignTitle,
        display: data.workflow.isWardAssReq,
        system: 'logo',
        by: [
          {
            title: 'Default',
            subTitle: data.workflow.defaultWardAssign,
          },
          ...wardAssignBy,
        ],
        color: 'yellowSolid',
      },
      {
        stopStation: 'Ward Acknowledged',
        display: data.workflow.isWardAckReq,
        system: 'logo',
        by: [
          {
            title: 'Ward',
          },
        ],
        color: 'yellow',
      },
      {
        stopStation: 'Ward Ready',
        display: data.workflow.isWardReadyReq,
        system: 'logo',
        by: [
          {
            title: 'Ward',
          },
        ],
        color: 'yellowSolid',
      },
      {
        stopStation: 'Admission Registration',
        display: 'Y',
        system: 'IPAS',
        by: [
          {
            title: data.workflow.admWardName,
          },
        ],
        color: 'yellowSolid',
      },
      {
        stopStation: 'Porter Dispatch',
        display: data.workflow.porterDispatchBy === 'ADS' ? 'Y' : 'N',
        system: data.workflow.porterDispatchBy,
        by: [
          {
            title: 'Porter',
          },
        ],
        color: 'yellowSolid',
      },
      {
        stopStation: 'AED Discharged',
        display: 'Y',
        system: 'eAED',
        by: [
          {
            title: data.workflow.aedWardName,
          },
        ],
        color: 'green',
      },
      {
        stopStation: 'Ward Arrival',
        display: data.workflow.porterDispatchBy === 'ADS' ? 'Y' : 'N',
        system: data.workflow.porterDispatchBy,
        by: [
          {
            title: 'Porter',
          },
        ],
        color: 'green',
      },
      {
        stopStation: 'Bed Assigned',
        display: 'Y',
        system: 'IPAS',
        by: [
          {
            title: 'Ward',
          },
        ],
        color: 'green',
      },
    ];
  }, [data]);

  const handleAfterSaveWorkflow = () => {
    getWorkflowList();
    setEdit(false);
  };

  return (
    <>
      {/* popup */}
      {edit && <EditWorkflow handleClose={() => setEdit(false)} handleAfterSave={handleAfterSaveWorkflow} />}
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="1.38rem">
          <Text color="#333" fontSize="1.5rem" fontFamily="NotoSans-Bold">
            Emergency Admission Workflow
          </Text>
          <Box display="flex" gridGap="1.25rem">
            <StyleButton text="Edit" onClick={() => setEdit(true)} Icon={EditIcon} width="6.9375rem" type="fill" />
          </Box>
        </Box>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '25%' }}>Bus stop station</TableCell>
              <TableCell style={{ width: '10%' }}>Show/hide</TableCell>
              <TableCell style={{ width: '10%' }}>System</TableCell>
              <TableCell style={{ width: '55%' }}>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workflow.map((item, index) => (
              <TableRow key={item.stopStation}>
                <TableCell className={classes.busStopStation}>
                  <StopStation
                    last={index === workflow.length - 1}
                    color={item.color}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    ml="2.13rem"
                    pr="1rem"
                    height="100%"
                    borderBottom="solid 1px #E4E7EB"
                  >
                    {item.stopStation}
                  </Box>
                </TableCell>
                <TableCell>
                  {item.display === 'Y' && <EyeIcon className={classes.eyeIcon} />}
                  {item.display === 'N' && <EyeHideIcon2 className={classes.eyeHideIcon} />}
                </TableCell>
                <TableCell>
                  {item.system === 'logo' ? <img className={classes.logo} src={Logo} alt="logo" /> : item.system}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gridGap="2.5rem">
                    {item.by.map((byItem) => (
                      <TitleBox title={byItem.title} key={byItem.key ?? byItem.title}>
                        <Text>{byItem.subTitle}</Text>
                      </TitleBox>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

export default Workflow;
